import axios from 'axios'
import Router from 'next/router'
import { destroyCookie, parseCookies } from 'nookies'
import { getToken, getRefreshToken, refreshToken, clear, getProfile } from './auth'
import toastControl from 'utils/toastControl'

import { cache } from './cache'

const API_BASE_URL = process.env.API

const TOKEN_ENDPOINTS = [`${API_BASE_URL}/token/`, `${API_BASE_URL}/prof_token/`]

const api = axios.create({
  adapter: cache.adapter,
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

let abortController = new AbortController()
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

api.interceptors.request.use((config) => {
  config.signal = abortController.signal

  const token = getToken()
  if (token) {
    config.headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`
  } else if (!TOKEN_ENDPOINTS.includes(config.url)) {
    console.warn('Requisição sem token para endpoint protegido')
  }

  const vittudeTotp = parseCookies()['vittude-totp']
  if (vittudeTotp) {
    config.headers['X-Vittude-Totp'] = vittudeTotp
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const refreshTokenValue = getRefreshToken()
    const profile = getProfile()
    const isPatient = profile?.role === 'patient'
    const errorLogCode = error.response?.data?.log_content?.error_code

    if (errorLogCode === 'mfa_required') {
      destroyCookie(null, 'vittude-totp')
      Router.push('/autenticacao')
      return Promise.reject(error)
    }

    const isTokenEndpoint = TOKEN_ENDPOINTS.includes(originalRequest.url)

    if (error?.response?.status === 401 && !isTokenEndpoint) {
      if (refreshTokenValue && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
              toastControl.showWarning('Sua sessão expirou. Por favor, faça login novamente.')
              clear()
              Router.push('/')
              reject(new Error('Tempo excedido para renovação do token'))
            }, 10000)

            failedQueue.push({
              resolve: (token) => {
                clearTimeout(timeoutId)
                originalRequest.headers.Authorization = token
                resolve(api(originalRequest))
              },
              reject: (err) => {
                clearTimeout(timeoutId)
                toastControl.show('Sua sessão expirou. Por favor, faça login novamente.')
                reject(err)
              }
            })
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          await refreshToken(refreshTokenValue, isPatient)

          const newToken = getToken()
          const formattedToken = newToken.startsWith('Bearer ') ? newToken : `Bearer ${newToken}`

          processQueue(null, formattedToken)
          originalRequest.headers.Authorization = formattedToken
          return api(originalRequest)
        } catch (refreshError) {
          toastControl.show('Sua sessão expirou. Por favor, faça login novamente.')
          processQueue(refreshError, null)
          clear()
          Router.push('/')
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      } else {
        toastControl.show('Sua sessão expirou. Por favor, faça login novamente.')
        clear()
        Router.push('/')
      }
    }

    return Promise.reject(error)
  }
)

export const cancelAllRequests = () => {
  abortController.abort()
  abortController = new AbortController()
}

export default api
